import { useAccount } from "wagmi";
import { formatUnits } from "viem";
import {
  Coins,
  Gift,
  TrendingUp,
  Plus,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { cn, formatTokenAmount, explorerUrl } from "../../lib/utils";
import { useRewards, type RewardSlotData } from "../../hooks/useRewards";
import { DJNN_TOKEN_ADDRESS } from "../../config/rewards";

/* ═══════════════════════════════════════════════════════════════════════════ */
/* REWARD SLOT ROW                                                            */
/* ═══════════════════════════════════════════════════════════════════════════ */

function RewardSlotRow({
  slot,
  onClaim,
  isClaiming,
  isConnected,
  index,
}: {
  slot: RewardSlotData;
  onClaim: () => void;
  isClaiming: boolean;
  isConnected: boolean;
  index: number;
}) {
  const hasPending = slot.pendingReward > 0n;
  const bpsPercent = Number(slot.bps) / 100;
  const pendingFormatted = formatTokenAmount(
    slot.pendingReward,
    slot.meta.decimals,
    slot.meta.decimals <= 8 ? 6 : 4
  );

  return (
    <div
      className="opacity-0 animate-fade-in flex items-center gap-3 p-3 rounded-button bg-surface/30 border border-border hover:border-border-active transition-all duration-200 group/row"
      style={{
        animationDelay: `${(index + 1) * 100}ms`,
        animationFillMode: "forwards",
      }}
    >
      {/* Token icon */}
      <div className="flex-shrink-0 relative">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-display font-bold select-none ring-2 ring-[#070816]"
          style={{
            background: `linear-gradient(135deg, ${slot.meta.color}, ${slot.meta.color}99)`,
            color: "#070816",
          }}
        >
          {slot.meta.symbol.slice(0, 2)}
        </div>
        {hasPending && (
          <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#9FFCE8] opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#9FFCE8]" />
          </span>
        )}
      </div>

      {/* Token info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-display font-semibold text-sm text-text-primary truncate">
            {slot.meta.symbol}
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary bg-white/[0.04] px-1.5 py-0.5 rounded">
            {bpsPercent.toFixed(1)}%
          </span>
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span
            className={cn(
              "text-sm font-display font-bold",
              hasPending ? "neon-text-orange" : "text-text-tertiary"
            )}
          >
            {pendingFormatted}
          </span>
          <a
            href={explorerUrl("address", slot.distributorAddress)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-text-tertiary hover:text-primary transition-colors opacity-0 group-hover/row:opacity-100"
          >
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>

      {/* Claim button */}
      <button
        onClick={onClaim}
        disabled={!hasPending || !isConnected || isClaiming}
        className={cn(
          "flex-shrink-0 px-3 py-1.5 rounded-button text-xs font-semibold transition-all duration-200",
          hasPending && isConnected
            ? "bg-primary/15 text-primary border border-primary/30 hover:bg-primary/25 hover:shadow-[0_0_12px_rgba(57,230,255,0.15)]"
            : "bg-white/[0.03] text-text-tertiary border border-white/[0.06] cursor-not-allowed opacity-40"
        )}
      >
        {isClaiming ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : (
          "Claim"
        )}
      </button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* EMPTY SLOT PLACEHOLDER                                                     */
/* ═══════════════════════════════════════════════════════════════════════════ */

function EmptySlotRow({ index }: { index: number }) {
  return (
    <div
      className="opacity-0 animate-fade-in flex items-center gap-3 p-3 rounded-button border border-dashed border-white/[0.06] bg-white/[0.01]"
      style={{
        animationDelay: `${(index + 5) * 100}ms`,
        animationFillMode: "forwards",
      }}
    >
      <div className="w-9 h-9 rounded-full flex items-center justify-center bg-white/[0.03] border border-dashed border-white/[0.08]">
        <Plus className="w-4 h-4 text-text-tertiary/50" />
      </div>
      <div className="flex-1">
        <span className="text-xs text-text-tertiary/50 font-medium">
          Available Slot
        </span>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* REWARD POOL CARD                                                           */
/* ═══════════════════════════════════════════════════════════════════════════ */

export function RewardPoolCard() {
  const { isConnected } = useAccount();
  const {
    slots,
    slotCount,
    activeSlotCount,
    emptySlotCount,
    djnnBalance,
    totalPendingCount,
    isLoading,
    isClaiming,
    claimSlot,
    claimAll,
  } = useRewards();

  const djnnFormatted = formatTokenAmount(djnnBalance, 18, 2);

  return (
    <div className="w-full max-w-[540px] mx-auto">
      <div
        className="relative group rounded-[18px] p-[2px] transition-all duration-300"
        style={{
          background:
            "linear-gradient(135deg, #39E6FF, #9FFCE8, #39E6FF)",
          boxShadow: totalPendingCount > 0
            ? "0 0 20px rgba(57,230,255,0.15), 0 0 60px rgba(57,230,255,0.05)"
            : undefined,
        }}
      >
        <div className="rounded-[16px] bg-[#070816] overflow-hidden">
          {/* ── Header ──────────────────────────────────────────────── */}
          <div className="px-5 pt-5 pb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{
                    background:
                      "linear-gradient(135deg, #39E6FF 0%, #9FFCE8 100%)",
                  }}
                >
                  <Gift className="w-5 h-5 text-[#070816]" />
                </div>
                <div>
                  <h2 className="gradient-text font-display font-bold text-xl leading-tight">
                    DJNN Rewards
                  </h2>
                  <span className="text-[10px] text-text-tertiary uppercase tracking-widest font-semibold">
                    ForgedDistributorV5
                  </span>
                </div>
              </div>
              <a
                href={explorerUrl("token", DJNN_TOKEN_ADDRESS)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-tertiary hover:text-primary transition-colors p-1.5 rounded-lg hover:bg-white/5"
                title="View DJNN on Explorer"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            {/* DJNN Balance + Slot Count */}
            <div className="flex items-center justify-between mb-1">
              <div>
                <span className="text-xs text-text-tertiary uppercase tracking-wider font-semibold block mb-1">
                  Your DJNN Balance
                </span>
                <span
                  className={cn(
                    "font-display text-2xl font-bold",
                    djnnBalance > 0n ? "neon-text-orange" : "text-text-tertiary"
                  )}
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin inline-block" />
                  ) : isConnected ? (
                    djnnFormatted
                  ) : (
                    "---"
                  )}
                </span>
              </div>
              <div className="text-right">
                <span className="text-xs text-text-tertiary uppercase tracking-wider font-semibold block mb-1">
                  Reward Slots
                </span>
                <span className="font-display text-lg font-bold text-text-primary">
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin inline-block" />
                  ) : (
                    <span>
                      <span className="text-primary">{activeSlotCount}</span>
                      <span className="text-text-tertiary">/{slotCount + emptySlotCount}</span>
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className="neon-divider mx-5" />

          {/* ── Reward Token Rows ───────────────────────────────────── */}
          <div className="px-5 py-4 space-y-2">
            {isLoading ? (
              <div className="flex flex-col items-center py-8">
                <Loader2 className="w-8 h-8 text-primary animate-spin mb-3" />
                <p className="text-sm text-text-tertiary">
                  Loading reward data...
                </p>
              </div>
            ) : slots.length > 0 ? (
              <>
                {/* Active reward slot rows */}
                {slots.map((slot, i) => (
                  <RewardSlotRow
                    key={slot.slot}
                    slot={slot}
                    onClaim={() => claimSlot(slot.distributorAddress)}
                    isClaiming={isClaiming}
                    isConnected={isConnected}
                    index={i}
                  />
                ))}

                {/* Empty slot placeholders */}
                {emptySlotCount > 0 && (
                  <div className="pt-2 space-y-2">
                    {Array.from({ length: Math.min(emptySlotCount, 3) }, (_, i) => (
                      <EmptySlotRow key={`empty-${i}`} index={i} />
                    ))}
                    {emptySlotCount > 3 && (
                      <div className="text-center py-1">
                        <span className="text-[10px] text-text-tertiary/40 font-medium">
                          +{emptySlotCount - 3} more available
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <Coins className="w-10 h-10 text-text-tertiary mx-auto mb-3" />
                <p className="text-sm text-text-secondary font-medium">
                  No reward distributors active
                </p>
                <p className="text-xs text-text-tertiary mt-1">
                  Reward slots have not been configured yet.
                </p>
              </div>
            )}
          </div>

          <div className="neon-divider mx-5" />

          {/* ── Footer Actions ──────────────────────────────────────── */}
          <div className="px-5 py-4 space-y-3">
            <button
              onClick={claimAll}
              disabled={
                !isConnected || totalPendingCount === 0 || isClaiming || isLoading
              }
              className={cn(
                "btn-primary w-full flex items-center justify-center gap-2 text-sm",
                (totalPendingCount === 0 || !isConnected) &&
                  "opacity-40 cursor-not-allowed"
              )}
            >
              {isClaiming ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Claiming...
                </>
              ) : (
                <>
                  <Gift className="w-4 h-4" />
                  Claim All Rewards
                  {totalPendingCount > 0 && (
                    <span className="bg-[#070816]/30 px-2 py-0.5 rounded-full text-xs font-bold">
                      {totalPendingCount}
                    </span>
                  )}
                </>
              )}
            </button>

            <button
              disabled
              className="btn-secondary w-full flex items-center justify-center gap-2 text-sm opacity-50 cursor-not-allowed"
              title="Only the contract owner can add reward slots"
            >
              <Plus className="w-4 h-4" />
              Add Token Slot
              <span className="text-[10px] uppercase tracking-wider text-text-tertiary bg-white/[0.04] px-1.5 py-0.5 rounded ml-1">
                Owner Only
              </span>
            </button>
          </div>

          {/* ── Total Distributed Stats ─────────────────────────────── */}
          {slots.length > 0 && (
            <>
              <div className="neon-divider mx-5" />
              <div className="px-5 py-4">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-3.5 h-3.5 text-text-tertiary" />
                  <span className="text-xs text-text-tertiary uppercase tracking-wider font-semibold">
                    Total Distributed
                  </span>
                </div>
                <div className="space-y-1.5">
                  {slots.map((slot) => {
                    const distFormatted = formatTokenAmount(
                      slot.totalDistributed,
                      slot.meta.decimals,
                      slot.meta.decimals <= 8 ? 6 : 2
                    );
                    return (
                      <div
                        key={slot.slot}
                        className="flex items-center justify-between text-sm"
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ background: slot.meta.color }}
                          />
                          <span className="text-text-secondary text-xs">
                            {slot.meta.symbol}
                          </span>
                        </div>
                        <span className="font-display font-semibold text-text-primary text-xs">
                          {distFormatted}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
